import type { AgreementFormSchema, AgreementFormValues } from "@/lib/agreement-form-schema";
import { normalizeAgreementFormSchema } from "@/lib/agreement-form-schema";

const configuredBaseUrl = import.meta.env.VITE_API_URL?.trim();
const configuredDevProxyTarget = import.meta.env.VITE_DEV_PROXY_TARGET?.trim();
const baseUrl = configuredBaseUrl ? configuredBaseUrl.replace(/\/+$/, "") : "/api";
const SESSION_STORAGE_KEY = "gupio_portal_session";

export type AccountStatus =
  | "approved"
  | "disabled"
  | "pending_approval"
  | "pending_email_verification"
  | "rejected";
export type SubmissionStatus = "draft" | "submitted" | "superseded";
export type EditRequestStatus = "approved" | "pending" | "rejected";
export type InternshipType = "paid" | "unpaid";

export interface PortalSummary {
  applicationsReceived: number;
  approvedCandidates: number;
  hiringMode: string;
  internshipTitle: string;
  nextCohort: string;
  openTracks: number;
  pendingCandidates: number;
  reviewWindow: string;
  submittedAgreements: number;
  tracks: string[];
}

export type CandidateAgreement = AgreementFormValues & {
  acceptedConfidentiality?: boolean;
  acceptedElectronicSignature?: boolean;
  acceptedTerms?: boolean;
  agreementDate?: string;
  cityState?: string;
  companyAddress?: string;
  confidentialityPeriod?: string;
  course?: string;
  endDate?: string;
  internAddress?: string;
  internName?: string;
  internshipType?: InternshipType;
  jurisdictionCity?: string;
  notes?: string;
  signatureDataUrl?: string;
  signatureLocation?: string;
  signatureName?: string;
  signedAt?: string;
  startDate?: string;
  stipendAmount?: string;
  university?: string;
  workingHours?: string;
  yearOfStudy?: string;
};

export type AgreementFormInput = AgreementFormValues & {
  acceptedConfidentiality: boolean;
  acceptedElectronicSignature: boolean;
  acceptedTerms: boolean;
  agreementDate: string;
  cityState: string;
  companyAddress: string;
  confidentialityPeriod: string;
  course?: string;
  endDate: string;
  internAddress: string;
  internshipType?: InternshipType;
  university: string;
  jurisdictionCity: string;
  notes: string;
  signatureDataUrl: string;
  signatureLocation: string;
  signatureName: string;
  signedAt: string;
  startDate: string;
  stipendAmount?: string;
  workingHours: string;
  yearOfStudy?: string;
};

export interface AgreementPreviewPdfInput {
  candidate: {
    email: string;
    fullName: string;
  };
  submission: Partial<AgreementFormInput>;
  template: Pick<
    TemplateVersion,
    | "agreementBody"
    | "companyAddress"
    | "companyName"
    | "dataProtectionNotice"
    | "termsAndConditions"
    | "title"
    | "versionNumber"
  >;
}

export interface CandidateUser {
  city: string;
  createdAt: string;
  email: string;
  emailVerifiedAt?: string;
  fullName: string;
  id: string;
  lastLoginAt?: string;
  mustChangePassword: boolean;
  phone: string;
  serialNumber: string;
  rejectionReason?: string;
  role: "candidate";
  sessionVersion: number;
  status: AccountStatus;
  university: string;
  updatedAt: string;
}

export interface AdminUser {
  createdAt: string;
  email: string;
  id: string;
  lastLoginAt?: string;
  mustChangePassword: boolean;
  name: string;
  role: "admin";
  sessionVersion: number;
  status: AccountStatus;
  updatedAt: string;
}

export interface TemplateVersion {
  agreementBody: string;
  assignedCandidateCount: number;
  assignedCandidateIds: string[];
  submittedCount: number;
  companyAddress: string;
  companyName: string;
  createdAt: string;
  dataProtectionNotice: string;
  defaults: {
    confidentialityPeriod: string;
    jurisdictionCity: string;
    workingHours: string;
  };
  description?: string;
  formSchema?: AgreementFormSchema;
  id: string;
  publishedAt?: string;
  status: "archived" | "draft" | "published";
  termsAndConditions: string;
  title: string;
  versionNumber: number;
}

export interface TemplateState {
  activeVersion: TemplateVersion;
  description?: string;
  id: string;
  slug: string;
  title: string;
  versions?: TemplateVersion[];
}

export interface AgreementSubmissionSummary {
  createdAt: string;
  id: string;
  sourceSubmissionId?: string;
  status: SubmissionStatus;
  approvalStatus?: "pending" | "approved" | "rejected";
  approvedAt?: string;
  rejectionReason?: string;
  reviewComment?: string;
  submittedAt?: string;
  summary: CandidateAgreement;
  templateVersionId: string;
  templateVersionNumber?: number;
  updatedAt: string;
  versionNumber: number;
}

export interface AgreementSubmissionDetail extends AgreementSubmissionSummary {
  formData: CandidateAgreement;
  pdfBase64?: string;
  pdfFileName?: string;
  pdfHash?: string;
  signatureHash?: string;
  approvedBy?: {
    displayName: string;
    email: string;
    id: string;
  };
  templateVersion?: TemplateVersion;
}

export interface EditRequest {
  admin?: {
    displayName: string;
    email: string;
    id: string;
  };
  candidate: CandidateUser;
  candidateId: string;
  createdAt: string;
  decisionComment?: string;
  decidedAt?: string;
  id: string;
  reasonCategory: string;
  reasonText: string;
  status: EditRequestStatus;
  submissionId: string;
  unlockedSubmissionId?: string;
  updatedAt: string;
}

export interface CandidateWorkspace {
  activeDraft: AgreementSubmissionDetail | null;
  editRequests: EditRequest[];
  latestSubmitted: AgreementSubmissionDetail | null;
  pendingEditRequest: EditRequest | null;
  template: TemplateState;
  user: CandidateUser;
  versions: AgreementSubmissionSummary[];
}

export interface CandidateDashboardResponse {
  user: CandidateUser;
  summary: {
    accountStatus: AccountStatus;
    accountStatusLabel: string;
    activeTemplateTitle: string;
    activeTemplateVersionNumber: number;
    latestSubmissionId: string | null;
    latestSubmissionUpdatedAt: string | null;
    latestSubmittedId: string | null;
    latestSubmittedStatus: "draft" | "submitted" | "not_submitted";
    pendingEditRequests: number;
  };
}

export interface DashboardResponse {
  auditTrail: Array<{
    action: string;
    actorEmail: string;
    actorRole: "admin" | "candidate" | "system";
    createdAt: string;
    id: string;
    metadata?: unknown;
    summary: string;
    targetId?: string;
    targetType?: string;
  }>;
  editRequests: EditRequest[];
  emailLogs: Array<{
    category: string;
    createdAt: string;
    errorMessage?: string;
    id: string;
    messageId?: string;
    provider?: string;
    status: string;
    subject: string;
    to: string;
  }>;
  submissions: Array<
    AgreementSubmissionSummary & {
      candidate: CandidateUser;
    }
  >;
  summary: {
    approvedCandidates: number;
    pendingAgreementApprovals: number;
    pendingApproval: number;
    pendingEditRequests: number;
    rejectedCandidates: number;
    submittedAgreements: number;
    totalCandidates: number;
  };
  template: TemplateState;
  users: Array<
    CandidateUser & {
      latestEditRequest?: EditRequest;
      latestApprovalActionAt?: string;
      latestApprovalDecision?: "approved" | "disabled" | "rejected";
      editRequestCount: number;
    }
  >;
}

export interface PendingAgreementSubmissionRow extends AgreementSubmissionSummary {
  candidate: CandidateUser;
  template: {
    slug: string;
    title: string;
  };
}

export interface CandidateAgreementsRow {
  candidate: CandidateUser;
  internshipSubmission: AgreementSubmissionSummary | null;
  addendumSubmission: AgreementSubmissionSummary | null;
}

export interface CandidateSignUpInput {
  acceptDataProcessingConsent: boolean;
  email: string;
  fullName: string;
  password: string;
  phone?: string;
  serialNumber?: string;
  university?: string;
}

export interface CandidateSignInInput {
  email: string;
  password: string;
}

export interface AdminSignInInput {
  email: string;
  password: string;
}

export type PasswordResetRole = "admin" | "candidate";

export interface CandidateOtpChallenge {
  challengeId: string;
  deliveryHint: string;
  developmentCode?: string;
  email: string;
  expiresAt: string;
  resendAvailableAt: string;
}

export interface PasswordResetChallenge extends CandidateOtpChallenge {
  role: PasswordResetRole;
}

type ApiValidationIssues = {
  fieldErrors?: Record<string, string[] | undefined>;
  formErrors?: string[];
};

type ApiErrorPayload = {
  issues?: ApiValidationIssues;
  message?: string;
};

export class ApiRequestError extends Error {
  fieldErrors: Record<string, string>;
  formErrors: string[];
  status: number;

  constructor(
    message: string,
    options: {
      fieldErrors?: Record<string, string>;
      formErrors?: string[];
      status: number;
    }
  ) {
    super(message);
    this.name = "ApiRequestError";
    this.fieldErrors = options.fieldErrors ?? {};
    this.formErrors = options.formErrors ?? [];
    this.status = options.status;
    Object.setPrototypeOf(this, ApiRequestError.prototype);
  }
}

export type PortalSession =
  | {
      expiresAt: string;
      role: "candidate";
      token: string;
      user: CandidateUser;
    }
  | {
      expiresAt: string;
      role: "admin";
      token: string;
      user: AdminUser;
    };

export const fallbackSummary: PortalSummary = {
  applicationsReceived: 24,
  approvedCandidates: 8,
  hiringMode: "Sign up -> email OTP verification -> admin approval -> agreement submission",
  internshipTitle: "Gupio Legal Agreement Portal",
  nextCohort: "May 2026",
  openTracks: 3,
  pendingCandidates: 11,
  reviewWindow: "2 to 5 working days",
  submittedAgreements: 13,
  tracks: ["Business Operations", "Research & Strategy", "Marketing & Growth"]
};

function readStoredSession() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(SESSION_STORAGE_KEY);

    if (!stored) {
      return null;
    }

    return JSON.parse(stored) as PortalSession;
  } catch {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    return null;
  }
}

function normalizeCandidateUser(raw: any): CandidateUser {
  return {
    id: raw.id,
    email: raw.email,
    fullName: raw.displayName,
    phone: raw.profile?.phone ?? "",
    serialNumber: raw.profile?.serialNumber ?? "",
    university: raw.profile?.university ?? "",
    city: raw.profile?.city ?? "",
    status: raw.status,
    role: "candidate",
    mustChangePassword: Boolean(raw.mustChangePassword),
    emailVerifiedAt: raw.emailVerifiedAt ?? undefined,
    lastLoginAt: raw.lastLoginAt ?? undefined,
    rejectionReason: raw.rejectionReason ?? undefined,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    sessionVersion: raw.sessionVersion ?? 0
  };
}

function normalizeAdminUser(raw: any): AdminUser {
  return {
    id: raw.id,
    email: raw.email,
    name: raw.displayName,
    role: "admin",
    status: raw.status,
    mustChangePassword: Boolean(raw.mustChangePassword),
    lastLoginAt: raw.lastLoginAt ?? undefined,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    sessionVersion: raw.sessionVersion ?? 0
  };
}

function normalizeSubmissionSummary(raw: any): AgreementSubmissionSummary {
  return {
    id: raw.id,
    versionNumber: raw.versionNumber,
    status: raw.status,
    approvalStatus: raw.approvalStatus ?? undefined,
    approvedAt: raw.approvedAt ?? undefined,
    rejectionReason: raw.rejectionReason ?? undefined,
    templateVersionId: raw.templateVersionId,
    templateVersionNumber: raw.templateVersionNumber ?? undefined,
    submittedAt: raw.submittedAt ?? undefined,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    sourceSubmissionId: raw.sourceSubmissionId ?? undefined,
    summary: (raw.summary ?? {}) as CandidateAgreement
  };
}

function normalizeSubmissionDetail(raw: any): AgreementSubmissionDetail {
  return {
    ...normalizeSubmissionSummary(raw),
    pdfBase64: raw.pdfBase64 ?? undefined,
    pdfFileName: raw.pdfFileName ?? undefined,
    pdfHash: raw.pdfHash ?? undefined,
    signatureHash: raw.signatureHash ?? undefined,
    formData: (raw.formData ?? {}) as CandidateAgreement,
    approvedBy: raw.approvedBy ?? undefined,
    templateVersion: raw.templateVersion ? normalizeTemplateVersion(raw.templateVersion) : undefined
  };
}

function normalizeEditRequest(raw: any): EditRequest {
  return {
    id: raw.id,
    candidateId: raw.candidateId,
    submissionId: raw.submissionId,
    reasonCategory: raw.reasonCategory,
    reasonText: raw.reasonText,
    status: raw.status,
    decisionComment: raw.decisionComment ?? undefined,
    decidedAt: raw.decidedAt ?? undefined,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    candidate: normalizeCandidateUser(raw.candidate),
    admin: raw.admin ?? undefined,
    unlockedSubmissionId: raw.unlockedSubmissionId ?? undefined
  };
}

function normalizeDashboardUser(raw: any): DashboardResponse["users"][number] {
  return {
    ...normalizeCandidateUser(raw),
    latestApprovalActionAt: raw.latestApprovalActionAt ?? undefined,
    latestApprovalDecision: raw.latestApprovalDecision ?? undefined,
    latestEditRequest: raw.latestEditRequest ? normalizeEditRequest(raw.latestEditRequest) : undefined,
    editRequestCount: raw.editRequestCount ?? 0
  };
}

function normalizeTemplateVersion(raw: any): TemplateVersion {
  return {
    id: raw.id,
    versionNumber: raw.versionNumber,
    status: raw.status,
    title: raw.title,
    description: raw.description ?? undefined,
    agreementBody: raw.agreementBody,
    termsAndConditions: raw.termsAndConditions,
    dataProtectionNotice: raw.dataProtectionNotice,
    companyName: raw.companyName,
    companyAddress: raw.companyAddress,
    defaults: raw.defaults,
    formSchema: normalizeAgreementFormSchema(raw.formSchema),
    assignedCandidateCount: raw.assignedCandidateCount ?? 0,
    assignedCandidateIds: Array.isArray(raw.assignedCandidateIds) ? raw.assignedCandidateIds : [],
    submittedCount: raw.submittedCount ?? 0,
    publishedAt: raw.publishedAt ?? undefined,
    createdAt: raw.createdAt
  };
}

function normalizeTemplate(raw: any): TemplateState {
  return {
    id: raw.id,
    slug: raw.slug,
    title: raw.title,
    description: raw.description ?? undefined,
    activeVersion: normalizeTemplateVersion(raw.activeVersion),
    versions: Array.isArray(raw.versions) ? raw.versions.map(normalizeTemplateVersion) : undefined
  };
}

function getUnavailableApiMessage() {
  return baseUrl.startsWith("/api")
    ? `Backend API is unavailable. Start the API server on ${configuredDevProxyTarget || "http://localhost:3001"} and try again.`
    : "Backend API is unavailable. Check that the configured API URL is reachable and try again.";
}

function normalizeApiFieldErrors(issues?: ApiValidationIssues) {
  return Object.entries(issues?.fieldErrors ?? {}).reduce<Record<string, string>>(
    (fieldErrors, [fieldName, messages]) => {
      const firstMessage = messages?.find((message) => typeof message === "string" && message.trim());

      if (firstMessage) {
        fieldErrors[fieldName] = firstMessage;
      }

      return fieldErrors;
    },
    {}
  );
}

async function request<T>(
  path: string,
  options?: RequestInit & {
    auth?: boolean;
  }
) {
  const headers = new Headers(options?.headers);
  const auth = options?.auth ?? false;
  const session = auth ? readStoredSession() : null;

  if (!headers.has("Content-Type") && options?.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  if (session?.token) {
    headers.set("Authorization", `Bearer ${session.token}`);
  }

  let response: Response;

  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers
    });
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(getUnavailableApiMessage());
    }

    throw error;
  }

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const textPayload = isJson ? null : await response.text();
  const payload = isJson ? ((await response.json()) as ApiErrorPayload & T) : null;

  if (!response.ok) {
    if (auth && response.status === 401 && typeof window !== "undefined") {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
    }

    if (response.status >= 500 && !isJson && typeof window !== "undefined") {
      throw new Error(getUnavailableApiMessage());
    }

    if (isJson) {
      const fieldErrors = normalizeApiFieldErrors(payload?.issues);
      const formErrors = payload?.issues?.formErrors?.filter(
        (message): message is string => typeof message === "string" && message.trim().length > 0
      ) ?? [];
      const errorMessage =
        formErrors[0] ?? Object.values(fieldErrors)[0] ?? payload?.message ?? "Request failed.";

      throw new ApiRequestError(errorMessage, {
        fieldErrors,
        formErrors,
        status: response.status
      });
    }

    const errorMessage = textPayload?.trim();
    throw new Error(errorMessage || "Request failed.");
  }

  return payload as T;
}

function getDownloadFileName(response: Response, fallback: string) {
  const contentDisposition = response.headers.get("content-disposition");

  if (!contentDisposition) {
    return fallback;
  }

  const match = /filename="?(?<name>[^"]+)"?/i.exec(contentDisposition);
  return match?.groups?.name ?? fallback;
}

export function fetchPortalSummary() {
  return request<PortalSummary>("/portal/summary");
}

export async function downloadAgreementPreviewPdf(payload: AgreementPreviewPdfInput) {
  const session = readStoredSession();

  if (!session?.token) {
    throw new Error("Please sign in again before downloading the agreement.");
  }

  let response: Response;

  try {
    response = await fetch(`${baseUrl}/portal/agreement-preview-pdf`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${session.token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(getUnavailableApiMessage());
    }

    throw error;
  }

  if (!response.ok) {
    if (response.status === 401 && typeof window !== "undefined") {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
    }

    const contentType = response.headers.get("content-type") ?? "";
    const errorMessage = contentType.includes("application/json")
      ? (((await response.json()) as { message?: string }).message ?? "Unable to download the agreement.")
      : (await response.text()).trim();

    throw new Error(errorMessage || "Unable to download the agreement.");
  }

  const blob = await response.blob();
  const fileName = getDownloadFileName(response, "gupio-internship-agreement-preview.pdf");
  const objectUrl = window.URL.createObjectURL(blob);
  const link = window.document.createElement("a");

  link.href = objectUrl;
  link.download = fileName;
  window.document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
}

export async function candidateSignUp(payload: CandidateSignUpInput) {
  return request<{
    challenge: CandidateOtpChallenge;
    message: string;
  }>("/auth/candidates/sign-up", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function verifyCandidateEmailOtp(payload: { challengeId: string; code: string }) {
  return request<{
    message: string;
    user: any;
  }>("/auth/candidates/verify-email-otp", {
    method: "POST",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    user: normalizeCandidateUser(response.user)
  }));
}

export function resendCandidateEmailOtp(payload: { challengeId: string }) {
  return request<{
    challenge: CandidateOtpChallenge;
    message: string;
  }>("/auth/candidates/resend-email-otp", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function candidateSignIn(payload: CandidateSignInInput) {
  return request<{
    expiresAt: string;
    message: string;
    token: string;
    user: any;
  }>("/auth/candidates/sign-in", {
    method: "POST",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    user: normalizeCandidateUser(response.user)
  }));
}

export function adminSignIn(payload: AdminSignInInput) {
  return request<{
    expiresAt: string;
    message: string;
    token: string;
    user: any;
  }>("/auth/admin/sign-in", {
    method: "POST",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    user: normalizeAdminUser(response.user)
  }));
}

export function requestPasswordReset(payload: { identifier: string; role: PasswordResetRole }) {
  return request<{ challenge?: CandidateOtpChallenge; message: string }>("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    challenge: response.challenge
      ? {
          ...response.challenge,
          role: payload.role
        }
      : undefined
  }));
}

export function verifyPasswordResetOtp(payload: { challengeId: string; code: string }) {
  return request<{ message: string; token: string }>("/auth/forgot-password/verify-otp", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function resendPasswordResetOtp(payload: { challengeId: string; role: PasswordResetRole }) {
  return request<{ challenge: CandidateOtpChallenge; message: string }>("/auth/forgot-password/resend-otp", {
    method: "POST",
    body: JSON.stringify({ challengeId: payload.challengeId })
  }).then((response) => ({
    ...response,
    challenge: {
      ...response.challenge,
      role: payload.role
    }
  }));
}

export function resetPassword(payload: { token: string; newPassword: string }) {
  return request<{ message: string }>("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export function changePassword(payload: { currentPassword: string; newPassword: string }) {
  return request<{
    expiresAt: string;
    message: string;
    token: string;
    user: any;
  }>("/auth/change-password", {
    auth: true,
    method: "POST",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    user: response.user.role === "admin"
      ? normalizeAdminUser(response.user)
      : normalizeCandidateUser(response.user)
  }));
}

export function logoutSession() {
  return request<{ message: string }>("/auth/logout", {
    auth: true,
    method: "POST"
  });
}

export function fetchCandidateWorkspace(templateSlug?: string) {
  const query = templateSlug ? `?templateSlug=${encodeURIComponent(templateSlug)}` : "";
  return request<any>(`/candidates/me/workspace${query}`, {
    auth: true
  }).then((response) => ({
    user: normalizeCandidateUser(response.user),
    template: normalizeTemplate(response.template),
    activeDraft: response.activeDraft ? normalizeSubmissionDetail(response.activeDraft) : null,
    latestSubmitted: response.latestSubmitted
      ? normalizeSubmissionDetail(response.latestSubmitted)
      : null,
    versions: Array.isArray(response.versions)
      ? response.versions.map(normalizeSubmissionSummary)
      : [],
    editRequests: Array.isArray(response.editRequests)
      ? response.editRequests.map(normalizeEditRequest)
      : [],
    pendingEditRequest: response.pendingEditRequest
      ? normalizeEditRequest(response.pendingEditRequest)
      : null
  })) as Promise<CandidateWorkspace>;
}

export function fetchCandidateDashboard(templateSlug?: string) {
  const query = templateSlug ? `?templateSlug=${encodeURIComponent(templateSlug)}` : "";
  return request<any>(`/candidates/me/dashboard${query}`, {
    auth: true
  }).then((response) => ({
    user: normalizeCandidateUser(response.user),
    summary: response.summary
  })) as Promise<CandidateDashboardResponse>;
}

export function saveAgreementDraft(payload: Partial<AgreementFormInput>, templateSlug?: string) {
  const query = templateSlug ? `?templateSlug=${encodeURIComponent(templateSlug)}` : "";
  return request<{
    message: string;
    submission: any;
  }>(`/candidates/me/agreement/draft${query}`, {
    auth: true,
    method: "PUT",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    submission: normalizeSubmissionDetail(response.submission)
  }));
}

export function submitAgreement(payload: AgreementFormInput, templateSlug?: string) {
  const query = templateSlug ? `?templateSlug=${encodeURIComponent(templateSlug)}` : "";
  return request<{
    message: string;
    submission: any;
  }>(`/candidates/me/agreement/submit${query}`, {
    auth: true,
    method: "POST",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    submission: normalizeSubmissionDetail(response.submission)
  }));
}

export function createEditRequest(payload: {
  reasonCategory: string;
  reasonText: string;
  submissionId: string;
}, templateSlug?: string) {
  const query = templateSlug ? `?templateSlug=${encodeURIComponent(templateSlug)}` : "";
  return request<{
    editRequest: any;
    message: string;
  }>(`/candidates/me/edit-requests${query}`, {
    auth: true,
    method: "POST",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    editRequest: normalizeEditRequest(response.editRequest)
  }));
}

export function fetchAdminDashboard(templateSlug?: string) {
  const query = templateSlug ? `?templateSlug=${encodeURIComponent(templateSlug)}` : "";
  return request<any>(`/admin/dashboard${query}`, {
    auth: true
  }).then((response) => ({
    summary: response.summary,
    users: Array.isArray(response.users) ? response.users.map(normalizeDashboardUser) : [],
    submissions: Array.isArray(response.submissions)
      ? response.submissions.map((item: any) => ({
          ...normalizeSubmissionSummary(item),
          candidate: normalizeCandidateUser(item.candidate)
        }))
      : [],
    editRequests: Array.isArray(response.editRequests)
      ? response.editRequests.map(normalizeEditRequest)
      : [],
    template: normalizeTemplate(response.template),
    auditTrail: response.auditTrail ?? [],
    emailLogs: response.emailLogs ?? []
  })) as Promise<DashboardResponse>;
}

export function fetchPendingAgreements() {
  return request<{ submissions: any[] }>("/admin/agreements/pending", {
    auth: true
  }).then((response) => ({
    submissions: Array.isArray(response.submissions)
      ? response.submissions.map((item: any) => ({
          ...normalizeSubmissionSummary(item),
          candidate: normalizeCandidateUser(item.candidate),
          template: item.template
        }))
      : []
  })) as Promise<{ submissions: PendingAgreementSubmissionRow[] }>;
}

export function fetchApprovedAgreements() {
  return request<{ submissions: any[] }>("/admin/agreements/approved", {
    auth: true
  }).then((response) => ({
    submissions: Array.isArray(response.submissions)
      ? response.submissions.map((item: any) => ({
          ...normalizeSubmissionSummary(item),
          candidate: normalizeCandidateUser(item.candidate),
          approvedBy: item.approvedBy ?? undefined,
          template: item.template
        }))
      : []
  })) as Promise<{ submissions: PendingAgreementSubmissionRow[] }>;
}

export function fetchCandidateAgreementsOverview() {
  return request<any>("/admin/candidates/agreements-overview", {
    auth: true
  }).then((response) => ({
    candidates: Array.isArray(response.candidates)
      ? response.candidates.map((row: any) => ({
          candidate: normalizeCandidateUser(row.candidate),
          internshipSubmission: row.internshipSubmission
            ? normalizeSubmissionSummary(row.internshipSubmission)
            : null,
          addendumSubmission: row.addendumSubmission
            ? normalizeSubmissionSummary(row.addendumSubmission)
            : null
        }))
      : []
  })) as Promise<{ candidates: CandidateAgreementsRow[] }>;
}

export function reviewAgreementSubmission(
  submissionId: string,
  payload: {
    decision: "approved" | "rejected";
    companyAddress?: string;
    authorizedSignatoryName?: string;
    authorizedSignatoryDesignation?: string;
    employeeName?: string;
    rejectionReason?: string;
    comment?: string;
  }
) {
  return request<{
    message: string;
    submission: any;
  }>(`/admin/submissions/${submissionId}/approval`, {
    auth: true,
    method: "PATCH",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    submission: normalizeSubmissionDetail(response.submission)
  }));
}

export function fetchAdminSubmissionDetail(submissionId: string) {
  return request<any>(`/admin/submissions/${submissionId}`, {
    auth: true
  }).then((response) => ({
    ...normalizeSubmissionDetail(response),
    candidate: normalizeCandidateUser(response.candidate),
    templateVersion: normalizeTemplateVersion(response.templateVersion)
  }));
}

export function updateCandidateApproval(
  candidateId: string,
  payload: { comment?: string; decision: "approved" | "rejected" }
) {
  return request<{
    message: string;
    user: any;
  }>(`/admin/candidates/${candidateId}/approval`, {
    auth: true,
    method: "PATCH",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    user: normalizeCandidateUser(response.user)
  }));
}

export function updateAdminCandidate(
  candidateId: string,
  payload: {
    email: string;
    fullName: string;
    phone?: string;
    status: AccountStatus;
  }
) {
  return request<{
    message: string;
    user: any;
  }>(`/admin/candidates/${candidateId}`, {
    auth: true,
    method: "PATCH",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    user: normalizeCandidateUser(response.user)
  }));
}

export function deleteAdminCandidate(candidateId: string) {
  return request<{ message: string }>(`/admin/candidates/${candidateId}`, {
    auth: true,
    method: "DELETE"
  });
}

export function saveTemplateDraft(
  payload: {
    agreementBody: string;
    companyAddress: string;
    companyName: string;
    dataProtectionNotice: string;
    defaultConfidentialityPeriod: string;
    defaultJurisdictionCity: string;
    defaultWorkingHours: string;
    description?: string;
    formSchema?: unknown;
    termsAndConditions: string;
    title: string;
  },
  templateSlug?: string
) {
  const query = templateSlug ? `?templateSlug=${encodeURIComponent(templateSlug)}` : "";
  return request<{
    message: string;
    version: any;
  }>(`/admin/templates/drafts${query}`, {
    auth: true,
    method: "POST",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    version: normalizeTemplateVersion(response.version)
  }));
}

export function updateTemplateDraft(
  versionId: string,
  payload: {
    agreementBody: string;
    companyAddress: string;
    companyName: string;
    dataProtectionNotice: string;
    defaultConfidentialityPeriod: string;
    defaultJurisdictionCity: string;
    defaultWorkingHours: string;
    description?: string;
    formSchema?: unknown;
    termsAndConditions: string;
    title: string;
  }
) {
  return request<{
    message: string;
    version: any;
  }>(`/admin/templates/${versionId}`, {
    auth: true,
    method: "PATCH",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    version: normalizeTemplateVersion(response.version)
  }));
}

export function publishTemplateVersion(
  versionId: string,
  payload?: {
    candidateIds?: string[];
    publishScope?: "all_approved_users" | "selected_users";
  }
) {
  return request<{
    message: string;
    version: any;
  }>(`/admin/templates/${versionId}/publish`, {
    auth: true,
    method: "POST",
    body: JSON.stringify(payload ?? {})
  }).then((response) => ({
    ...response,
    version: normalizeTemplateVersion(response.version)
  }));
}

export function deleteTemplateVersion(versionId: string) {
  return request<{
    message: string;
    versionId: string;
  }>(`/admin/templates/${versionId}`, {
    auth: true,
    method: "DELETE"
  });
}

export function reviewEditRequest(
  requestId: string,
  payload: { decision: "approved" | "rejected"; decisionComment?: string }
) {
  return request<{
    editRequest: any;
    message: string;
  }>(`/admin/edit-requests/${requestId}`, {
    auth: true,
    method: "PATCH",
    body: JSON.stringify(payload)
  }).then((response) => ({
    ...response,
    editRequest: normalizeEditRequest(response.editRequest)
  }));
}

export interface PdfDetectedField {
  fieldKey: string;
  label: string;
  type: "shortText" | "longText" | "date" | "email" | "dropdown";
  required: boolean;
  isReserved: boolean;
}

export interface PdfDetectedSection {
  title: string;
  fields: PdfDetectedField[];
}

export interface PdfAnalysisResult {
  sections: PdfDetectedSection[];
  totalFields: number;
}

export function analyzePdfForFields(pdfBase64: string): Promise<PdfAnalysisResult> {
  return request<PdfAnalysisResult>("/admin/templates/analyze-pdf", {
    auth: true,
    method: "POST",
    body: JSON.stringify({ pdfBase64 })
  });
}
